import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalSizes,
  ButtonVariants,
  Stepper,
  Table,
  Alert,
  AlertVariants,
  Dropdown,
  Typography,
  TypographyVariants,
  TypographyColors,
  TypographyElements,
} from '@carboledger/ui';
import { useTranslations, UnitTypeContext, useUnitTypeHeader, resolveRowUnitType } from '@/hooks';
import { DiscrepancyModal } from '../Discrepancy';
import { SummaryCard } from '../SummaryCard';
import { useGetShipmentDiscrepancyQuery } from '@/lib/api/linksApi';
import { buildShipmentDiscrepancySections } from './linkShipmentsDiscrepancy';
import { shipmentUnits, contractUnits } from './linkUnits';
import { assignedTotalInUnit } from './linkFields';
import { rowUnitSymbol } from '@/lib/mappers/quantities';
import { buildTableLabels } from '@/config/tableLabels';
import { buildIncomingShipmentFilterDefinitions } from '@/config/filterDefinitions';
import {
  buildLinkStep1Columns,
  buildLinkStep2Columns,
  buildLinkContractColumns,
} from './linkShipmentsColumns';
import { EMPTY_LIST } from '@/lib/utils';
import { buildSuggestionGroups, getSuggestionGroup } from '../pickerGroups';
import styles from './LinkShipmentsModal.module.scss';

// Which way round the link goes; it decides the step-1 picker and the step-2 rows.
export const LINK_FLOWS = Object.freeze({
  shipmentsToContract: 'shipmentsToContract',
  contractsToShipment: 'contractsToShipment',
  shipmentsToDeclaration: 'shipmentsToDeclaration',
});

// Opened from the contracts toolbar with rows already ticked — one shipment onto them all.
export const isBulkFlow = (flow) => flow === LINK_FLOWS.contractsToShipment;

const SELECT_STEP = 0;
const ASSIGN_STEP = 1;
const getRowId = (row) => row.id;

/**
 * Props:
 *   open          whether the modal is shown
 *   onClose       close handler (✕ / Cancel / Escape)
 *   title         the modal's heading, already carrying the contract id
 *   detailFields  (unitType, assignedInUnit) => GENERAL DETAILS card rows, so the card
 *                 reads in the unit the dropdown below it is on; `assignedInUnit(unitType)`
 *                 is step 2's running total, which an Outstanding quantity row deducts
 *   shipments     linkable-shipment row view models
 *   loading       whether the shipment list is still fetching
 *   flow          one of LINK_FLOWS — many shipments onto one contract, or one
 *                 shipment onto the many selected contracts
 *   bulkRows      (the bulk flow) the contract rows the list had ticked, which step 2
 *                 shows instead of the shipments
 *   isSaving      disables Save while the mutation is in flight
 *   isRowDisabled (row) => bool — step 1 greys the row and locks its checkbox
 *   onSearchChange (term) => void — the list is filtered server-side
 *   onSave        (entries, pickedShipmentId) => void — entries are
 *                 `{ rowId, quantity, unitSymbol }`, the unit being the row's own
 */
export const LinkShipmentsModal = ({
  open,
  onClose,
  title,
  detailFields,
  shipments = EMPTY_LIST,
  loading = false,
  flow = LINK_FLOWS.shipmentsToContract,
  bulkRows = EMPTY_LIST,
  isSaving = false,
  isRowDisabled,
  onSearchChange,
  onFilterChange,
  onSave,
}) => {
  const t = useTranslations();
  const [step, setStep] = useState(SELECT_STEP);
  const [selectedIds, setSelectedIds] = useState(EMPTY_LIST);
  const [pickedShipmentId, setPickedShipmentId] = useState(null);
  const [assignments, setAssignments] = useState({});
  // The Table's key — it owns its search, selection and page, so only a remount clears them.
  const [session, setSession] = useState(0);
  const [discrepancyRow, setDiscrepancyRow] = useState(null);
  const [error, setError] = useState(null);

  // Bulk picks ONE shipment for the ticked rows; the other flow ticks many for one contract.
  const isBulk = isBulkFlow(flow);

  // Which shipments step 2 carries over — the radio's one, or every ticked row.
  const step2ShipmentIds = useMemo(
    () => (isBulk ? [pickedShipmentId].filter((id) => id != null) : selectedIds),
    [isBulk, pickedShipmentId, selectedIds],
  );

  // Step 2's rows: the chosen shipments, or — in a bulk flow — the rows the list ticked.
  const step1Rows = shipments;
  const step2Rows = useMemo(() => {
    if (isBulk) return bulkRows.filter((row) => assignments[row.id] !== undefined);
    return shipments.filter((row) => step2ShipmentIds.includes(row.id));
  }, [isBulk, bulkRows, shipments, step2ShipmentIds, assignments]);

  const unitRows = step === SELECT_STEP ? step1Rows : step2Rows;
  const unitOptions =
    step === ASSIGN_STEP && isBulk
      ? contractUnits.buildUnitHeaderOptions
      : shipmentUnits.buildUnitHeaderOptions;
  const { unitHeaderSelect, contextValue, selections } = useUnitTypeHeader(
    unitRows,
    unitOptions,
    t,
  );

  // The builder asks in the basis it renders, which is not always the header's.
  const assignedInUnit = useCallback(
    (unit) => assignedTotalInUnit(step2Rows, assignments, selections, unit),
    [step2Rows, assignments, selections],
  );

  // The card reads in whatever unit the header dropdown is on, exactly as the table does.
  const detailRows = useMemo(
    () => detailFields?.(unitHeaderSelect.value, assignedInUnit) ?? EMPTY_LIST,
    [detailFields, unitHeaderSelect.value, assignedInUnit],
  );

  useEffect(() => {
    if (!open) return;
    setStep(SELECT_STEP);
    setSelectedIds(EMPTY_LIST);
    setPickedShipmentId(null);
    setAssignments({});
    setError(null);
    setSession((previous) => previous + 1);
    setDiscrepancyRow(null);
    onSearchChange?.('');
    onFilterChange?.({});
    // onSearchChange is the parent's setter; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = useCallback(() => onClose?.(), [onClose]);

  const handleSelectionChange = useCallback((rows, ids) => setSelectedIds(ids), []);
  const handleOpenDiscrepancy = useCallback((row) => setDiscrepancyRow(row), []);
  const handleCloseDiscrepancy = useCallback(() => setDiscrepancyRow(null), []);

  // currentData, not data: `data` keeps the previous shipment while a new one loads.
  const { currentData: discrepancyShipment, isFetching: isDiscrepancyLoading } =
    useGetShipmentDiscrepancyQuery(discrepancyRow?.id, { skip: !discrepancyRow });

  const discrepancySections = useMemo(
    () =>
      buildShipmentDiscrepancySections(
        t,
        discrepancyShipment,
        discrepancyRow && resolveRowUnitType(discrepancyRow, selections),
      ),
    [t, discrepancyShipment, discrepancyRow, selections],
  );
  const handlePick = useCallback((id) => setPickedShipmentId(id), []);

  const handleAssign = useCallback((rowId, quantity) => {
    setAssignments((prev) => ({ ...prev, [rowId]: quantity }));
  }, []);

  const filterDefinitions = useMemo(() => buildIncomingShipmentFilterDefinitions(t), [t]);

  const handleFilterChange = useCallback((values) => onFilterChange?.(values), [onFilterChange]);

  // Server-side: `search` is a variable on the linkable-shipments query.
  const handleSearchChange = useCallback((value) => onSearchChange?.(value), [onSearchChange]);

  const handleRemove = useCallback((rowId) => {
    setAssignments((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  }, []);

  // Seeding a blank assignment per row is also what puts a contract row on step 2.
  const goNext = useCallback(() => {
    const rows = isBulk ? bulkRows : shipments.filter((r) => step2ShipmentIds.includes(r.id));
    // Only the typed quantity is state; the unit follows the row's own selection.
    setAssignments(Object.fromEntries(rows.map((row) => [row.id, ''])));
    setError(null);
    setStep(ASSIGN_STEP);
  }, [isBulk, bulkRows, shipments, step2ShipmentIds]);

  const goBack = useCallback(() => {
    setStep(SELECT_STEP);
    setError(null);
  }, []);

  const handleSave = useCallback(() => {
    // The row's shown unit, so the payload matches the suffix beside the typed number.
    const entries = step2Rows
      .map((row) => {
        const unitType = resolveRowUnitType(row, selections);
        return {
          rowId: row.id,
          quantity: assignments[row.id],
          unitSymbol: rowUnitSymbol(row, unitType),
        };
      })
      .filter((entry) => {
        const amount = Number(entry.quantity);
        return entry.quantity !== '' && Number.isFinite(amount) && amount > 0;
      });

    if (entries.length === 0) {
      setError(t('LINK_ASSIGN_REQUIRED'));
      return;
    }
    const missingUnit = entries.find((entry) => !entry.unitSymbol);
    if (missingUnit) {
      setError(t('LINK_SHIPMENTS_ASSIGN_UNIT_MISSING'));
      return;
    }
    setError(null);
    onSave?.(entries, isBulk ? pickedShipmentId : null);
  }, [step2Rows, selections, assignments, onSave, isBulk, pickedShipmentId, t]);

  const steps = useMemo(
    () => [{ label: t('LINK_SHIPMENTS_STEP_SELECT') }, { label: t('LINK_STEP_ASSIGN') }],
    [t],
  );

  const step1Columns = useMemo(
    () =>
      buildLinkStep1Columns(t, {
        headerSelect: unitHeaderSelect,
        selectMode: isBulk ? 'single' : 'multi',
        selectedId: pickedShipmentId,
        onSelect: handlePick,
        onOpenDiscrepancy: handleOpenDiscrepancy,
      }),
    [t, unitHeaderSelect, isBulk, pickedShipmentId, handlePick, handleOpenDiscrepancy],
  );

  const step2Columns = useMemo(
    () =>
      isBulk
        ? buildLinkContractColumns(t, {
            headerSelect: unitHeaderSelect,
            assignments,
            onAssign: handleAssign,
            onRemove: handleRemove,
          })
        : buildLinkStep2Columns(t, {
            headerSelect: unitHeaderSelect,
            assignments,
            onAssign: handleAssign,
            onOpenDiscrepancy: handleOpenDiscrepancy,
          }),
    [t, isBulk, assignments, handleAssign, handleRemove, unitHeaderSelect, handleOpenDiscrepancy],
  );

  const emptyState = useMemo(() => ({ title: t('LINK_SHIPMENTS_EMPTY') }), [t]);
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);
  // TODO: group the declaration flow once linkableShipmentsForDeclaration exists server-side.
  const isGrouped = flow !== LINK_FLOWS.shipmentsToDeclaration;
  const step1Groups = useMemo(
    () => (isGrouped ? buildSuggestionGroups(t) : EMPTY_LIST),
    [isGrouped, t],
  );
  const getStep1RowGroup = isGrouped ? getSuggestionGroup : undefined;

  const canAdvance = isBulk ? pickedShipmentId != null : selectedIds.length > 0;

  const buttons = useMemo(() => {
    if (step === SELECT_STEP) {
      return [
        { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: handleClose },
        {
          label: t('COMMON_NEXT'),
          variant: ButtonVariants.primary,
          disabled: !canAdvance,
          onClick: goNext,
        },
      ];
    }
    return [
      { label: t('COMMON_PREVIOUS'), variant: ButtonVariants.ghost, onClick: goBack },
      { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: handleClose },
      {
        label: t('COMMON_SAVE'),
        variant: ButtonVariants.primary,
        disabled: isSaving,
        loading: isSaving,
        onClick: handleSave,
      },
    ];
  }, [t, step, canAdvance, handleClose, goNext, goBack, handleSave, isSaving]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size={ModalSizes.large}
      title={title}
      buttons={buttons}
    >
      <div className={styles.body}>
        <Stepper steps={steps} activeStep={step} variant="flush" className={styles.stepper} />

        <SummaryCard heading={t('SECTION_GENERAL_DETAILS')} fields={detailRows} />

        {error ? <Alert variant={AlertVariants.danger}>{error}</Alert> : null}

        {/* TODO: use the library Tooltip once it ships. */}
        {!loading && step === SELECT_STEP && step1Rows.length === 0 ? (
          <Alert variant={AlertVariants.warning} title={t('LINK_SHIPMENTS_NONE_TITLE')}>
            {t('LINK_SHIPMENTS_NONE_BODY')}
          </Alert>
        ) : null}

        <UnitTypeContext.Provider value={contextValue}>
          {step === SELECT_STEP ? (
            <Table
              key={`select-${session}`}
              columns={step1Columns}
              data={step1Rows}
              loading={loading}
              getRowId={getRowId}
              searchable
              onSearchChange={handleSearchChange}
              filters={filterDefinitions}
              onFilterChange={handleFilterChange}
              selectable={!isBulk}
              isRowDisabled={isRowDisabled}
              onSelectionChange={handleSelectionChange}
              emptyState={emptyState}
              labels={tableLabels}
              groups={step1Groups}
              getRowGroup={getStep1RowGroup}
            />
          ) : (
            <>
              {!isBulk ? (
                <div className={styles.unitField}>
                  <Typography
                    as={TypographyElements.span}
                    variant={TypographyVariants.bodyXs}
                    color={TypographyColors.secondary}
                  >
                    {t('LINK_ASSIGN_UNIT_LABEL')}
                  </Typography>
                  {/* The same set-every-row control the unit column's header uses. */}
                  <Dropdown
                    value={unitHeaderSelect.value}
                    onChange={unitHeaderSelect.onChange}
                    options={unitHeaderSelect.options}
                    aria-label={t('LINK_ASSIGN_UNIT_LABEL')}
                  />
                </div>
              ) : null}
              <Table
                key={`assign-${session}`}
                columns={step2Columns}
                data={step2Rows}
                getRowId={getRowId}
                showPagination={false}
                emptyState={emptyState}
                labels={tableLabels}
              />
            </>
          )}
        </UnitTypeContext.Provider>
      </div>

      <DiscrepancyModal
        open={discrepancyRow != null}
        onClose={handleCloseDiscrepancy}
        referenceId={discrepancyShipment?.shipmentId}
        discrepancies={discrepancyShipment?.discrepancies}
        sections={discrepancySections}
        loading={isDiscrepancyLoading}
      />
    </Modal>
  );
};
